import React, { useEffect, useState } from 'react'
import classnames from 'classnames'
import { stickyPanelHeaderClassName } from '../stickyPanel'

// Collapsible section card (v001 §3) — shows summary totals inline in the
// header when collapsed. Same surface conventions as SaberCard.

export interface CollapsibleCardProps {
  title: React.ReactNode
  /** Rendered right-aligned in the header row (shown collapsed and expanded). */
  summary?: React.ReactNode
  defaultOpen?: boolean
  /** When expandAllVersion changes, all cards sync to this open state (Expand all / Collapse all). */
  expandAllOpen?: boolean
  expandAllVersion?: number
  onOpenChange?: (open: boolean) => void
  /** Pin the header while scrolling; `top` is set from the measured page header height. */
  stickyOffset?: number
  /** Stacking order when multiple sticky headers overlap (later sections should be higher). */
  stickyZIndex?: number
  children?: React.ReactNode
  className?: string
}

export const CollapsibleCard: React.FC<CollapsibleCardProps> = ({
  title,
  summary,
  defaultOpen = false,
  expandAllOpen,
  expandAllVersion = 0,
  onOpenChange,
  stickyOffset,
  stickyZIndex,
  children,
  className
}) => {
  const [open, setOpen] = useState(defaultOpen)

  useEffect(() => {
    if (expandAllVersion > 0 && expandAllOpen != null) setOpen(expandAllOpen)
  }, [expandAllVersion, expandAllOpen])

  const toggleOpen = (): void => {
    setOpen((prev) => {
      const next = !prev
      onOpenChange?.(next)
      return next
    })
  }

  const stickyHeader = stickyOffset != null

  return (
    <section
      className={classnames(
        'relative rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm',
        className
      )}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={toggleOpen}
        className={classnames(
          'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors',
          stickyHeader
            ? classnames(
                stickyPanelHeaderClassName,
                'overflow-hidden hover:bg-slate-50 dark:hover:bg-slate-700/40',
                open ? 'rounded-t-lg' : 'rounded-lg'
              )
            : classnames(
                'relative z-10 hover:bg-slate-50 dark:hover:bg-slate-700/40',
                open ? 'rounded-t-lg' : 'rounded-lg'
              )
        )}
        style={stickyHeader ? { top: stickyOffset, zIndex: stickyZIndex ?? 9 } : undefined}
      >
        <span className="inline-flex items-center gap-2">
          <span
            aria-hidden
            className={classnames('inline-block w-3 text-slate-400 transition-transform', open && 'rotate-90')}
          >
            ▸
          </span>
          <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</span>
        </span>
        {summary != null && (
          <span className="ml-auto flex flex-wrap items-center justify-end gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
            {summary}
          </span>
        )}
      </button>
      {open && (
        <div className="overflow-hidden border-t border-slate-200 p-4 dark:border-slate-700">{children}</div>
      )}
    </section>
  )
}

export default CollapsibleCard
